import {
  createContext,
  PropsWithChildren,
  useContext,
  useState,
  useCallback,
} from "react";
import { StoreApi, createStore, useStore } from "zustand";

// components
import { toast, ToastContainer } from "react-toastify";

// types
import { IOrganizations } from "../../setup/types/Organisation";
import {
  deleteOrganizationAPI,
  getAllOrganizations,
} from "../../setup/api/services/organization";

interface OrganizationStore {
  loading: boolean;
  page: number;
  pageCount: number;
  organizations?: IOrganizations[];
  selectedOrganisation?: IOrganizations;
  setLoading: (value: boolean) => void;
  buttonLoading: boolean;
  setButtonLoading: (value: boolean) => void;
  setPage: (value: number) => void;
  setPageCount: (value: number) => void;
  setOrganizations: (organizations: IOrganizations[]) => void;
  setSelectedOrganization: (organization?: IOrganizations) => void;
  searchOrganisation: (value: string) => void;
  deleteOrganisation: (value: string) => void;
}

const OrganisationContext = createContext<
  StoreApi<OrganizationStore> | undefined
>(undefined);

type OrganisationProviderProps = PropsWithChildren;

const OrganstionProvider = ({ children }: OrganisationProviderProps) => {
  const [store] = useState(() =>
    createStore<OrganizationStore>((set) => ({
      organizations: undefined,
      selectedOrganisation: undefined,
      loading: true,
      buttonLoading: false,
      page: 1,
      pageCount: 1,
      setLoading: (value: boolean) => {
        set({ loading: value });
      },
      setPage: (page: number) => {
        set({ page });
      },
      setPageCount: (value: number) => {
        set({ pageCount: value });
      },
      setSelectedOrganization: (organization?: IOrganizations) => {
        set({ selectedOrganisation: organization ? organization : undefined });
      },
      setOrganizations: (organizations) => {
        set({ organizations: organizations });
      },
      searchOrganisation: (searchValue) => {
        fetchOrganizationsList(searchValue);
      },
      deleteOrganisation: (code: string) => {
        deleteOrganisation(code);
      },
      setButtonLoading: (value: boolean) => {
        set({ buttonLoading: value });
      },
    }))
  );

  const pageSize = 10;

  const fetchOrganizationsList = useCallback(
    async (searchValue) => {
      store.getState().setLoading(true);
      try {
        const response = await getAllOrganizations(
          store?.getState().page - 1,
          pageSize,
          searchValue
        );
        store.getState().setOrganizations(response.data.content);
        store.getState().setPageCount(response.data.totalPages);
      } catch (error) {
        console.error;
      } finally {
        store.getState().setLoading(false);
      }
    },
    [store]
  );

  const refreshPage = useCallback(() => {
    window?.location?.reload();
  }, []);

  const deleteOrganisation = useCallback(
    async (code: string) => {
      store.getState().setButtonLoading(true);
      try {
        await deleteOrganizationAPI(code, { code: code });
        refreshPage();
      } catch (error) {
        toast(`Error ${JSON.stringify(error)}`);
        store.getState().setButtonLoading(false);
      }
    },
    [refreshPage, store]
  );

  return (
    <OrganisationContext.Provider value={store}>
      {children}
      {/* ─── Toast Container ───────────────────────────── */}
      <ToastContainer />
    </OrganisationContext.Provider>
  );
};

export default OrganstionProvider;

export function useOrganisationStore<T>(
  selector: (state: OrganizationStore) => T
) {
  const context = useContext(OrganisationContext);
  if (!context) {
    throw new Error("Organisation Provider is missing");
  }

  return useStore(context, selector);
}
